import type { IHistoryItemDto } from '@domain/Vehicle/History/IHistoryItem';
import { HttpBase } from '@library/Security';
import type { IConditionDto, IServiceInterval } from '@domain/Vehicle';
import { useUser } from '@domain/Security/User';
import type { Dayjs } from 'dayjs';
import type { ServiceIntervalState } from "@domain/Vehicle";
import type {IServiceIntervalDto} from "@domain/Vehicle/ServiceSchedule";
import { CarbucksVehicleEventType } from '@domain/Vehicle/History/CarbucksVehicleEventType';
import { isDemo } from '@modules/Utilities/Environments';

interface IConditionApi
{
    advanceByConditionInterval( conditionId: string ): Promise<UpdateConditionResponse|null>;
    setNext( conditionId: string, nextDate: Dayjs, advanceFromToday: boolean ): Promise<UpdateConditionResponse|null>;
    setServiceInterval( conditionId: string, serviceInterval: IServiceInterval, lastOdometer: number, lastMonths: number ): Promise<UpdatetServiceIntervalResponse|null>;
    setServiceIntervalState( serviceScheduleId: string, serviceIntervalId: string, state: ServiceIntervalState ): Promise<IServiceIntervalDto|null>;
    resolve( conditionId: string ): Promise<IConditionDto|null>;
    dismiss( conditionId: string ): Promise<IConditionDto|null>;
    enable( conditionId: string ): Promise<IConditionDto|null>;
    disable( conditionId: string ): Promise<IConditionDto|null>;
}

class ConditionApi extends HttpBase implements IConditionApi
{

    private getPayload = ( otherParams: object ) =>
    {
        const user = useUser();
        return {
            vehicleId: user?.activeVehicle.vehicleId,
            ...otherParams
        };
    };

    /**
     * Updates a vehicle condition by setting its last date to today and its next date advanced
     * from today by the interval of the condition type
     * @param conditionId
     * @returns
     */
    async advanceByConditionInterval( conditionId: string ): Promise<UpdateConditionResponse|null>
    {
        const payload = this.getPayload( { conditionId: conditionId } );
        const result = await this.put( 'api/condition/advance-by-condition-interval', payload );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Updates a vehicle condition by setting the date that it is next due
     * @param conditionId
     * @param nextDate
     * @returns
     */
    async setNext( conditionId: string, nextDate: Dayjs, advanceFromToday: boolean ): Promise<UpdateConditionResponse|null>
    {
        const payload = this.getPayload( { conditionId: conditionId, nextDate: nextDate, advanceFromToday: advanceFromToday } );
        const result = await this.put( 'api/condition/set-next', payload );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Updates a vehicle service condition by setting its position from a service interval
     * @param conditionId
     * @returns
     */
    async setServiceInterval( conditionId: string, serviceInterval: IServiceInterval, lastOdometer: number, lastMonths: number ): Promise<UpdatetServiceIntervalResponse|null>
    {
        const payload = this.getPayload( {
            conditionId: conditionId,
            serviceIntervalId: serviceInterval.serviceIntervalId,
            lastOdometer: lastOdometer,
            lastMonths: lastMonths
        } );
        const result = await this.put( 'api/serviceschedule/set-active-service-interval', payload );
        if( result.success ) return result.data;
        return null;
    }

    async setServiceIntervalState( serviceScheduleId: string, serviceIntervalId: string, state: ServiceIntervalState ): Promise<IServiceIntervalDto|null>
    {
        const payload = this.getPayload( {
            serviceScheduleId: serviceScheduleId,
            serviceIntervalId: serviceIntervalId,
            state: state
        } );

        const result = await this.post( 'api/serviceschedule/set-service-interval-state', payload );

        return result.success ? result.data : null;
    }

    /**
     * Resolves a critical alert on a condition and puts it back into its state based on its interval
     * @param conditionId
     * @returns
     */
    async resolve( conditionId: string ): Promise<IConditionDto|null>
    {
        const payload = this.getPayload( { conditionId: conditionId } );
        const result = await this.post( 'api/condition/resolve-alert', payload );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Dismisses a critical alert on a condition and puts it back into its state based on its interval
     * @param conditionId
     * @returns
     */
    async dismiss( conditionId: string ): Promise<IConditionDto|null>
    {
        const payload = this.getPayload( { conditionId: conditionId } );
        const result = await this.post( 'api/condition/dismiss-alert', payload );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Marks the condition as enabled for the user
     * @param conditionId
     * @returns
     */
    async enable( conditionId: string ): Promise<IConditionDto|null>
    {
        const payload = { conditionId: conditionId };
        const result = await this.post( 'api/condition/enable', payload );
        return result.success ? result.data : null;
    }

    /**
     * Marks the condition as disabled for the user
     * @param conditionId
     * @returns
     */
    async disable( conditionId: string ): Promise<IConditionDto|null>
    {
        const payload = { conditionId: conditionId };
        const result = await this.post( 'api/condition/disable', payload );
        return result.success ? result.data : null;
    }
}

/**
 * Mocks all the endpoints of the ConditionApi.
 * Returns null for all endpoints atm as each call to the API is set up to handle when a response is not returned.
 * Null is used to prevent any UI updates from occurring on the screen, this is mostly because after the 'API' change
 * is made, the UI refreshes the My Garage View Model, which is causing the mock data to override any changes made
 * by calling this API in the first place. Hence there is no point in returning any data.
 * Also, console.log() has been added to each call to show that the 'API' is being called (for ensuring that Demo mode is working)
 */
class DemoConditionApi implements IConditionApi
{
    private resolveWith<T>( obj: T | null ): Promise<T | null>
    {
        return new Promise( ( resolve ) =>
        {
            resolve( obj );
        } );
    }

    advanceByConditionInterval(conditionId: string): Promise<UpdateConditionResponse | null>
    {
        console.log( 'ConditionApi.advanceByConditionInterval() called' );
        return this.resolveWith<UpdateConditionResponse>( null );
    }
    setNext(conditionId: string, nextDate: Dayjs, advanceFromToday: boolean): Promise<UpdateConditionResponse | null>
    {
        console.log( 'ConditionApi.setNext() called' );
        return this.resolveWith<UpdateConditionResponse>( null );
    }
    setServiceInterval(conditionId: string, serviceInterval: IServiceInterval, lastOdometer: number, lastMonths: number): Promise<UpdatetServiceIntervalResponse | null>
    {
        console.log( 'ConditionApi.setServiceInterval() called' );
        return this.resolveWith<UpdatetServiceIntervalResponse>( null );
    }
    setServiceIntervalState(serviceScheduleId: string, serviceIntervalId: string, state: ServiceIntervalState): Promise<IServiceIntervalDto | null>
    {
        console.log( 'ConditionApi.setServiceIntervalState() called' );
        return this.resolveWith<IServiceIntervalDto>( null );
    }
    resolve(conditionId: string): Promise<IConditionDto | null>
    {
        console.log( 'ConditionApi.resolve() called' );
        return this.resolveWith<IConditionDto>( null );
    }
    dismiss(conditionId: string): Promise<IConditionDto | null>
    {
        console.log( 'ConditionApi.dismiss() called' );
        return this.resolveWith<IConditionDto>( null );
    }
    enable(conditionId: string): Promise<IConditionDto | null>
    {
        console.log( 'ConditionApi.enable() called' );
        return this.resolveWith<IConditionDto>( null );
    }
    disable(conditionId: string): Promise<IConditionDto | null>
    {
        console.log( 'ConditionApi.disable() called' );
        return this.resolveWith<IConditionDto>( null );
    }
}

export const useConditionApi = () => isDemo() ? new DemoConditionApi() : new ConditionApi();

/* API specific request/response models */

type HaveHistoryItem = {
    historyItem: IHistoryItemDto;
};

/* used when updating or marking a condition as done */
type UpdateConditionResponse = {
    condition: IConditionDto;
    historyItem: IHistoryItemDto;
}

/* used when setting the service interval condition as done */
type UpdatetServiceIntervalResponse = HaveHistoryItem & {
    condition: IConditionDto;
    historyItem: IHistoryItemDto | null;
}

interface AdvanceToNextServiceIntervalRequest {
    vehicleId: string;
    conditionId: string;
    currentServiceIntervalId: string;
}
